import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import withSizes from 'react-sizes';
import VisibilitySensor from 'react-visibility-sensor';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { trackFavoriteAction } from 'containers/Favorites/utils';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
} from 'containers/Main/selectors';
import { getSavedTopics, saveTopic } from 'containers/Topics/actions';
import { makeSelectSavedTopics } from 'containers/Topics/selectors';
import { isTopicSaved } from 'containers/Topics/utils';
import Carousel from 'components/ResourcesCarousel';
import { getFormattedType } from 'components/ResourceItemSelector/utils';
import TopicCardCTA from 'components/TopicCardCTA';
import Mixpanel from 'utils/mixpanelService';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: 21,
  },
  label: {
    marginBottom: 22,
    [theme.breakpoints.down('xs')]: {
      ...theme.typography.h5,
    },
  },
  viewMore: {
    [theme.breakpoints.down('480')]: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      textAlign: 'center',
      margin: 0,
      padding: '150px 0 40px 0 !important',
      backgroundImage:
        'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.74), rgba(255,255,255,1))',
    },
  },
  listWrapper: {
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    position: 'relative',
    '& .react-multi-carousel-track': {
      padding: '16px 0px',
    },
    '& > div': {
      marginBottom: 12,
    },
  },
}));

const stateSelector = createStructuredSelector({
  allowSignIn: makeSelectAllowSignIn(),
  auth: makeSelectAuth(),
  clientDetails: makeSelectClientDetails(),
  savedTopics: makeSelectSavedTopics(),
});

function TopicsList(props) {
  const { topics, isMobile, isTablet, pageData } = props;
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const { allowSignIn, auth, clientDetails, savedTopics } = useSelector(
    stateSelector,
  );

  useEffect(() => {
    dispatch(getSavedTopics());
  }, []);

  if (_isEmpty(topics)) {
    return null;
  }

  const onSaveTopic = (topicID, fields, saved, e) => {
    e.preventDefault();
    e.stopPropagation();

    const { slug, title, phi } = fields;

    let nodeID;
    if (saved) [{ nodeID }] = savedTopics.filter(el => el.sys.id === topicID);

    const data = {
      nodeID,
      topic: {
        topicID,
        slug,
        name: title,
      },
      save: !saved,
      phi,
    };
    const pageName = location.pathname.split('/')[1];
    const finalPageName = `${pageName} Page`;
    trackFavoriteAction(
      'Card Click',
      {
        page: finalPageName,
        type: 'Topics',
        resourceID: topicID,
        slug,
        name: title,
        path: window.location.pathname,
      },
      auth,
      saved,
    );

    dispatch(saveTopic(data));
    return false;
  };

  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  const excludedTopicId = !_isEmpty(clientExcludedTopic)
    ? clientExcludedTopic.map(({ sys }) => sys.id)
    : [];
  const filteredData = topics.filter(
    el =>
      !_isEmpty(_get(el, 'fields.reviewStatus')) &&
      el.fields.reviewStatus === 'Accepted' &&
      !excludedTopicId.includes(el.sys.id),
  );
  const finalTopics = isMobile ? filteredData : filteredData.slice(0, 3);

  const content = () => {
    if (isMobile) {
      return finalTopics.map(topic => {
        const hasFields = _has(topic, 'fields');
        const saved = isTopicSaved(savedTopics, topic);
        const finalTopicFields = hasFields ? topic.fields : topic;
        return (
          <TopicCardCTA
            id={topic.id || topic.sys.id}
            fields={finalTopicFields}
            allowSignIn={allowSignIn}
            isSaved={saved}
            onSave={onSaveTopic}
            key={finalTopicFields.slug}
            section="blog-page"
            showLearnMore
            removeShadow
          />
        );
      });
    }
    return (
      <Grid container spacing={2}>
        {finalTopics.map(topic => {
          const hasFields = _has(topic, 'fields');
          const saved = isTopicSaved(savedTopics, topic);
          const finalTopicFields = hasFields ? topic.fields : topic;
          return (
            <Grid item xs={12} sm={6} md={12} key={finalTopicFields.slug}>
              <TopicCardCTA
                id={topic.id || topic.sys.id}
                fields={finalTopicFields}
                allowSignIn={allowSignIn}
                isSaved={saved}
                onSave={onSaveTopic}
                section="blog-page"
                showLearnMore
                removeShadow
                fullHeight={isTablet}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  };
  const visibilityChange = isVisible => {
    const {
      fields,
      fields: { blog, name, title },
      sys,
    } = pageData[0];
    let type = getFormattedType(sys.contentType.sys.id);
    if (blog) type = 'blog';
    else if (_get(fields, 'type') === 'News') type = 'news';
    if (isVisible) {
      Mixpanel.track('Resource Page - ScrollTo', {
        path: window.location.pathname,
        section: 'related-topics-panel',
        type,
        name: name || title,
      });
    }
  };

  const responsive = {
    desktop: {
      breakpoint: {
        max: 3000,
        min: 1024,
      },
      items: 4,
    },
    mobile: {
      breakpoint: {
        max: 600,
        min: 0,
      },
      items: 1,
      partialVisibilityGutter: 15,
    },
    tablet: {
      breakpoint: {
        max: 1024,
        min: 600,
      },
      items: 2,
      partialVisibilityGutter: 25,
    },
  };

  const customProps = isTablet ? { partialVisbile: 'right' } : null;
  let finalSlideToShow;
  if (isMobile) {
    finalSlideToShow = 1;
  } else if (isTablet) {
    finalSlideToShow = 2;
  } else {
    finalSlideToShow = 4;
  }
  const filteredDataLen = filteredData.length;
  if (_isEmpty(filteredData)) return null;
  return (
    <div className={classes.root}>
      <Grid item xs={12}>
        <Typography variant="h6" color="textPrimary" className={classes.label}>
          Take Your Next Step
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.listWrapper}>
        <Hidden xsDown>
          <Grid container spacing={1}>
            {content()}
          </Grid>
        </Hidden>
        <Hidden smUp>
          <VisibilitySensor
            onChange={visibilityChange}
            scrollCheck
            scrollDelay={500}
          >
            {filteredDataLen > 1 ? (
              <Carousel
                responsive={responsive}
                customProps={customProps}
                slidesToSlide={finalSlideToShow}
                infinite
                outsideArrow
              >
                {content()}
              </Carousel>
            ) : (
              <>{content()}</>
            )}
          </VisibilitySensor>
        </Hidden>
      </Grid>
    </div>
  );
}

TopicsList.propTypes = {
  topics: PropTypes.object,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  pageData: PropTypes.array,
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width <= 600,
  isTablet: width <= 1024,
});

export default withSizes(mapSizesToProps)(TopicsList);
